'use client';
import { useState, type HTMLAttributes } from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import { FormProvider, useController, useForm } from 'react-hook-form';
import { z } from 'zod'
import { zodResolver } from "@hookform/resolvers/zod";

import { getRoute } from '@/utils/utils';
import { Hx } from '@/stories/atoms/Hx/Hx';
import { Button } from '@/stories/atoms/Button/Button';
import { Icon } from '@/stories/atoms/Icon/Icon';
import { Loader } from '@/stories/atoms/Loader/Loader';
import { Input } from '@/stories/components/Form/Input/Input';
import { Checkbox } from '@/stories/components/Form/Checkbox/Checkbox';
import { Textarea } from '@/stories/components/Form/Textarea';
import { SimpleForm } from '@/stories/components/Form/Form';
import { BottomBar } from '@/stories/organisms/BottomBar/BottomBar';
import { BarButton } from '@/stories/organisms/BottomBar/BarButton';
import { Grid } from '../../Grid/Grid';

import styles from './ask-annonce-infos.module.css';
import { getConsentText } from '@/domains/form/form.utils';
import { sendAskInfosForm } from '@/domains/form/form.api';
import useIsMobile from '@/hooks/useMobile';
import { Row } from '../../Grid/Row';
import { Radio } from '@/stories/components/Form/Radio/Radio';
import { Label } from '@/stories/components/Form/FormItem/Label';


const Content = ({agence, isProgram}: {agence?: string, isProgram?: boolean}) => {

  const {
    field: firstName,
    fieldState: { error: firstNameError },
  } = useController({
    name: 'firstName'
  })

  const {
    field: lastName,
    fieldState: { error: lastNameError },
  } = useController({
    name: 'lastName'
  })

    const {
    field: phone,
    fieldState: { error: phoneError },
  } = useController({
    name: 'phone'
  })

  const {
    field: email,
    fieldState: { error: emailError },
  } = useController({
    name: 'email'
  })

  const {
    field: type,
  } = useController({
    name: 'type',
  })

  const {
    field: message,
    fieldState: { error: messageError },
  } = useController({
    name: 'message'
  })

  const {
    field: consent,
    fieldState: { error: consentError },
  } = useController({
    name: 'consent',
  })

  return (
    <>
      <Grid nbCols={2}>
        <Input
          value={firstName.value || ''}
          setValue={(value: string) => firstName.onChange(value)}
          errorText={firstNameError?.message}
          label='Prénom'
          placeholder='John'
          required/>
        <Input
          value={lastName.value || ''}
          setValue={(value: string) => lastName.onChange(value)}
          errorText={lastNameError?.message}
          label='Nom'
          placeholder='DOE'
          required/>
      </Grid>
      <Grid nbCols={2}>
        <Input
          value={phone.value || ''}
          setValue={(value: string) => phone.onChange(value)}
          errorText={phoneError?.message}
          label='Téléphone'
          placeholder='06 XX XX XX XX'
          icon='phone'
          required/>
        <Input
          value={email.value || ''}
          setValue={(value: string) => email.onChange(value)}
          errorText={emailError?.message}
          label='Email'
          placeholder='john.doe@gmail.com'
          icon='envelope'
          required/>
      </Grid>
      {isProgram && (
        <Grid nbCols={1} gap='xs'>
          <Label label='Je souhaite' hideOptionnal/>
          <Row>
            <Radio
              label={'Habiter'}
              name='type'
              onChange={() => type.onChange(true)}
              defaultChecked={type.value === true}
              />
          <Radio
            label={'Investir'}
            name='type'
            onChange={() => {
              type.onChange(false)
              type.onChange(false)
            }}
            defaultChecked={type.value === false}
            />
          </Row>
        </Grid>
      )}
      <Textarea
        value={message.value}
        setValue={(value: string) => message.onChange(value)}
        errorText={messageError?.message}
        label='Message'
        required/>
      <Checkbox
        onChange={() => consent.onChange(!consent.value)}
        checked={consent.value || false}
        errorText={consentError?.message}
        label={
          <>J’accepte que mes données soient utilisées par {agence} pour répondre à ma demande, conformément à la <Link href={getRoute({key: 'privacy-policy'})}>Politique de confidentialité.</Link></>
        }
        required/>
      <Button type='submit'>Envoyer un message<Icon icon='envelope'/></Button>
    </>
  )
}


export const AskAnnonceInfos = ({agence, phone, id, isProgram, ...props}: HTMLAttributes<HTMLElement> & {
  agence?: string,
  phone?: string,
  id: string,
  isProgram?: boolean
}) => {
  const [open, setOpen] = useState(false)
  const [send, setSend] = useState(false)
  const [loader, setLoader] = useState(false)
  const isMobile = useIsMobile()

  const validationSchema = z.object({
    firstName: z.string({ required_error: 'Prénom est requis' }).min(1, 'Prénom est requis'),
    lastName: z.string({ required_error: 'Nom est requis' }).min(1, 'Nom est requis'),
    email: z.string({ required_error: 'Email est requis' }).email('Email invalide'),
    phone: z.string({ required_error: 'Téléphone est requis' }),
    message: z.string({ required_error: 'Message est requis' }).min(1, 'Message est requis'),
    ...(isProgram ? {type: z.boolean({ required_error: 'Type ets requis est requis' })} : {}),
    consent: z.boolean({ required_error: 'Consentement est requis' }),
  })

  const toggleForm = () => {
    setOpen(!open)
    document.body.classList.toggle('has-dialog')
  }

  const methods = useForm({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
    defaultValues: {
      message: isProgram
        ? `Bonjour, je souhaite obtenir plus d'informations concernant ce programme...`
        : `Bonjour, je souhaite obtenir plus d'informations concernant ce bien...`
    }
  })

  const { handleSubmit, setError } = methods;
  
  const onSubmit = async (values: Partial<z.infer<typeof validationSchema>>) => {
    setLoader(true)
    // envoie form
    const data = await sendAskInfosForm({
      venteReferenceUnique: id,
      message: values.message || '',
      contact: {
        email: values.email || '',
        prenom: values.firstName || '',
        nom: values.lastName || '',
        telephone: values.phone || ''
      },
      consent: {
        privacyConsentGiven: values.consent || false,
        consentText: getConsentText(agence),
        privacyConsentAt: new Date().toISOString(),
        consentLanguage: 'fr',
        privacyPolicyUrl: getRoute({key: 'privacy-policy'}),
        privacyPolicyVersion: 'v1',
      }
    })
    setLoader(false)
    if (data.errors) {
      const errors = {
        ...(data.errors.message ? {message: data.errors.message} : {}),
        ...(data.errors?.['contact.prenom'] ? {firstName: data.errors['contact.prenom'][0]} : {}),
        ...(data.errors?.['contact.nom'] ? {lastName: data.errors?.['contact.nom'][0]} : {}),
        ...(data.errors?.['contact.email'] ? {email: data.errors?.['contact.email'][0]} : {}),
        ...(data.errors?.['contact.telephone'] ? {phone: data.errors?.['contact.telephone'][0]} : {}),
      }

      Object.keys(errors).map(
        (error) => setError(
          error as keyof typeof errors,
          {
            type: "custom",
            message: errors[error as keyof typeof errors]
          }
      ))
    } else {
      setSend(true)
    }
  }

  return (
    <>
      <BottomBar>
        <BarButton id='open-contact' onClick={toggleForm} onAuxClick={toggleForm} aria-expanded={open}><>Contacter<Icon icon='message'/></></BarButton>
        <BarButton url={`tel:${phone}`}><>Appeler<Icon icon='phone'/></></BarButton>
      </BottomBar>
      <div className={clsx(styles.wrapper, open && styles.open, props.className)} {...(isMobile ? {'aria-labelledby': 'open-contact'} : {})}>
        <BottomBar>
          <BarButton onClick={toggleForm}><>Fermer<Icon icon='close'/></></BarButton>
        </BottomBar>
        {send ? (
          <Hx headingLevel='h3' styleLevel='h4' className={styles.confirm}>Votre demande a été envoyée.</Hx>
        ) : loader ? <Loader /> : (
          <>
            <Hx headingLevel='h3' styleLevel='h4'>Demande d’information</Hx>
            <FormProvider {...methods}>
              <SimpleForm onSubmit={handleSubmit(onSubmit)}>
                <Content agence={agence} isProgram={isProgram}/>
              </SimpleForm>
            </FormProvider>
          </>
        )}
      </div>
    </>
  )
};
